import React from "react";
import "./MaintenancePage.css";

/**
 * Wartungsseite (portiert aus dem statischen Projekt siemens-maintenance).
 * Wird angezeigt, wenn der Wartungsmodus (ENABLE_MAINTENANCE) aktiv ist und
 * ein Nicht-Admin die Anwendung aufruft (Weiterleitung via Interceptor auf 503).
 */
function MaintenancePage() {
    const rootRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const cardRef = React.useRef(null);

    React.useEffect(() => {
        const root = rootRef.current;
        const canvas = canvasRef.current;
        const card = cardRef.current;
        if (!root || !canvas) return undefined;

        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const ctx = canvas.getContext("2d");

        // Shared pointer state (smoothed)
        const pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2, active: false };
        const smooth = { x: pointer.x, y: pointer.y };

        const onPointerMove = (e) => {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
            root.style.setProperty("--mx", pointer.x + "px");
            root.style.setProperty("--my", pointer.y + "px");
        };
        const onPointerLeave = () => { pointer.active = false; };
        window.addEventListener("pointermove", onPointerMove, { passive: true });
        window.addEventListener("pointerleave", onPointerLeave);

        // 1) Particle network
        let W = 0, H = 0, dpr = 1;
        let particles = [];

        function seed() {
            // Density scales with viewport area, capped for performance.
            const target = Math.min(140, Math.round((W * H) / 14000));
            particles = [];
            for (let i = 0; i < target; i++) {
                particles.push({
                    x: Math.random() * W,
                    y: Math.random() * H,
                    vx: (Math.random() - 0.5) * 0.35,
                    vy: (Math.random() - 0.5) * 0.35,
                    r: Math.random() * 1.6 + 0.6,
                });
            }
        }

        function resize() {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            W = window.innerWidth;
            H = window.innerHeight;
            canvas.width = Math.floor(W * dpr);
            canvas.height = Math.floor(H * dpr);
            canvas.style.width = W + "px";
            canvas.style.height = H + "px";
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            seed();
        }

        const LINK_DIST = 130;      // max distance to draw a link between two nodes
        const CURSOR_RADIUS = 200;  // influence radius of the pointer

        let rafId = null;
        function step() {
            ctx.clearRect(0, 0, W, H);

            // Smooth the pointer for gentle following motion.
            smooth.x += (pointer.x - smooth.x) * 0.08;
            smooth.y += (pointer.y - smooth.y) * 0.08;

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                p.x += p.vx;
                p.y += p.vy;

                // Cursor repulsion — particles gently push away from the pointer.
                if (pointer.active) {
                    const dx = p.x - smooth.x;
                    const dy = p.y - smooth.y;
                    const dist = Math.hypot(dx, dy);
                    if (dist < CURSOR_RADIUS && dist > 0.001) {
                        const force = (1 - dist / CURSOR_RADIUS) * 0.9;
                        p.x += (dx / dist) * force;
                        p.y += (dy / dist) * force;
                    }
                }

                // Wrap around edges for a continuous field.
                if (p.x < -20) p.x = W + 20;
                else if (p.x > W + 20) p.x = -20;
                if (p.y < -20) p.y = H + 20;
                else if (p.y > H + 20) p.y = -20;

                // Node dot
                const nearCursor = pointer.active && Math.hypot(p.x - smooth.x, p.y - smooth.y) < CURSOR_RADIUS;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = nearCursor ? "rgba(100, 255, 218, 0.9)" : "rgba(0, 204, 204, 0.55)";
                ctx.fill();

                // Link to the cursor itself
                if (pointer.active) {
                    const cd = Math.hypot(p.x - smooth.x, p.y - smooth.y);
                    if (cd < CURSOR_RADIUS) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(smooth.x, smooth.y);
                        ctx.strokeStyle = "rgba(0, 255, 185," + (0.25 * (1 - cd / CURSOR_RADIUS)) + ")";
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }
                }

                // Links to neighbours
                for (let j = i + 1; j < particles.length; j++) {
                    const q = particles[j];
                    const dx = p.x - q.x;
                    const dy = p.y - q.y;
                    const d = Math.hypot(dx, dy);
                    if (d < LINK_DIST) {
                        const near = pointer.active &&
                            (Math.hypot(p.x - smooth.x, p.y - smooth.y) < CURSOR_RADIUS ||
                             Math.hypot(q.x - smooth.x, q.y - smooth.y) < CURSOR_RADIUS);
                        const base = (1 - d / LINK_DIST);
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(q.x, q.y);
                        ctx.strokeStyle = near
                            ? "rgba(100, 255, 218," + (0.45 * base) + ")"
                            : "rgba(0, 153, 153," + (0.18 * base) + ")";
                        ctx.lineWidth = near ? 1.1 : 0.7;
                        ctx.stroke();
                    }
                }
            }

            rafId = requestAnimationFrame(step);
        }

        resize();
        window.addEventListener("resize", resize);
        if (!reduceMotion) {
            rafId = requestAnimationFrame(step);
        } else {
            // Static single frame so the field is still visible.
            step();
            if (rafId) cancelAnimationFrame(rafId);
            rafId = null;
        }

        // 2) Orb parallax
        const orbs = Array.from(root.querySelectorAll(".orb"));
        let parallaxId = null;
        function parallax() {
            const cx = window.innerWidth / 2;
            const cy = window.innerHeight / 2;
            const ox = (smooth.x - cx);
            const oy = (smooth.y - cy);
            for (const orb of orbs) {
                const depth = parseFloat(orb.dataset.depth || "0.04");
                orb.style.transform = `translate(${ox * depth}px, ${oy * depth}px)`;
            }
            parallaxId = requestAnimationFrame(parallax);
        }
        if (!reduceMotion) parallaxId = requestAnimationFrame(parallax);

        // 3) 3D tilt on the card
        let onCardMove = null;
        let onCardLeave = null;
        if (card && !reduceMotion) {
            const MAX_TILT = 6; // degrees
            onCardMove = (e) => {
                const rect = card.getBoundingClientRect();
                const px = (e.clientX - rect.left) / rect.width - 0.5;
                const py = (e.clientY - rect.top) / rect.height - 0.5;
                card.style.transform =
                    `rotateY(${px * MAX_TILT}deg) rotateX(${-py * MAX_TILT}deg) translateZ(0)`;
            };
            onCardLeave = () => { card.style.transform = "rotateY(0) rotateX(0)"; };
            card.addEventListener("pointermove", onCardMove);
            card.addEventListener("pointerleave", onCardLeave);
        }

        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerleave", onPointerLeave);
            window.removeEventListener("resize", resize);
            if (rafId) cancelAnimationFrame(rafId);
            if (parallaxId) cancelAnimationFrame(parallaxId);
            if (card && onCardMove) card.removeEventListener("pointermove", onCardMove);
            if (card && onCardLeave) card.removeEventListener("pointerleave", onCardLeave);
        };
    }, []);

    return (
        <div className="maintenance-page" ref={rootRef}>
            <canvas className="network" ref={canvasRef} aria-hidden="true"/>
            <div className="spotlight" aria-hidden="true"/>
            <div className="orbs" aria-hidden="true">
                <span className="orb orb--1" data-depth="0.03"/>
                <span className="orb orb--2" data-depth="0.06"/>
                <span className="orb orb--3" data-depth="0.045"/>
            </div>

            <div className="page">
                <header className="topbar">
                    <span className="status-pill">
                        <span className="status-pill__dot" aria-hidden="true"/>
                        System status: Maintenance
                    </span>
                </header>

                <main className="stage">
                    <section className="card" ref={cardRef} aria-labelledby="headline">
                        <p className="card__eyebrow">Scheduled maintenance</p>
                        <h1 className="card__title" id="headline">
                            We&rsquo;re upgrading<br/>
                            <span className="grad">the experience.</span>
                        </h1>
                        <p className="card__lead">
                            Our services are temporarily offline while we roll out improvements to
                            performance, security and reliability. Thanks for your patience &mdash;
                            we&rsquo;ll be back online very soon.
                        </p>
                    </section>
                </main>

                <footer className="footer">
                    <span className="footer__legal">&copy; 2026 Siemens AG &middot; All rights reserved</span>
                </footer>
            </div>
        </div>
    );
}

export default MaintenancePage;
